import APIRequester from "src/api/APIRequester"
import LoginHelper from "src/components/login/LoginHelper"
import AuthenticationError from "src/validation/exception_messages/AuthenticationError"
import RedirectToLoginException from "src/validation/exception_messages/RedirectToLoginException"
import FeatureProvider from "src/features/FeatureProvider"
import FeatureFlagIds from "src/features/FeatureFlagIds"
import FeatureFlag from "src/features/FeatureFlag"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import posthog from "src/consent/posthog"


class ApiHandler {
    baseURL: string 
    comparableHomesURL: string 
    valuateURL: string 
    findAddressURL: string 
    suggestAddressURL: string 
    getAddressURL: string 
    getUserInfoURL: string 
    getAddressImageURL: string 
    saveValuationURL: string
    updateSavedValuationValueURL: string
    listValuationsURL: string
    deleteValuationURL: string
    createCheckoutURL: string
    findAreaURL: string
    suggestAreaURL: string
    valuationUsageURL: string
    globalUsageURL: string
    areaSignalsURL: string
    nearbyAgentsURL: string
    getAgentLogoURL: string
    agentEnquiryURL: string
    sendVerificationCodeURL: string
    verifyPhoneURL: string
    refinedValuationResearchURL: string
    listRefinedValuationsURL: string
    deleteRefinedValuationURL: string
    updateRefinedValuationURL: string
    searchComparablesURL: string
    getListingPhotosURL: string
    consumeListingURL: string
    getRefinedValuationURL: string
    apiRequester: APIRequester
    loginHelper: LoginHelper
    stateHandler: EventAdaptingStateHandler
    requestCache: Map<string, any>
    featureProvider: FeatureProvider
    private imageQueue: Promise<any> = Promise.resolve()
    private inFlightImages: Map<string, Promise<any>> = new Map()
    private agentLogoQueue: Promise<any> = Promise.resolve()
    private inFlightAgentLogos: Map<string, Promise<any>> = new Map()

    constructor(apiRequester: APIRequester, baseURL: string, loginHelper: LoginHelper, stateHandler: EventAdaptingStateHandler, featureProvider: FeatureProvider) {
        this.apiRequester = apiRequester
        this.baseURL = baseURL
        this.comparableHomesURL = this.baseURL + "/comparable_homes"
        this.valuateURL = this.baseURL + "/valuate"
        this.findAddressURL = this.baseURL + "/find_address"
        this.suggestAddressURL = this.baseURL + "/suggest_address"
        this.getUserInfoURL = this.baseURL + "/get_user_info"
        this.getAddressImageURL = this.baseURL + "/get_address_image"
        this.getAddressURL = this.baseURL + "/get_address"
        this.saveValuationURL = this.baseURL + "/save_valuation"
        this.updateSavedValuationValueURL = this.baseURL + "/update_saved_valuation_value"
        this.listValuationsURL = this.baseURL + "/list_valuations"
        this.deleteValuationURL = this.baseURL + "/delete_valuation"
        this.createCheckoutURL = this.baseURL + "/create_checkout"
        this.findAreaURL = this.baseURL + "/find_area"
        this.suggestAreaURL = this.baseURL + "/suggest_area"
        this.valuationUsageURL = this.baseURL + "/valuation_usage"
        this.globalUsageURL = this.baseURL + "/global_usage"
        this.areaSignalsURL = this.baseURL + "/area_signals"
        this.nearbyAgentsURL = this.baseURL + "/nearby_agents"
        this.getAgentLogoURL = this.baseURL + "/get_agent_logo"
        this.agentEnquiryURL = this.baseURL + "/agent_enquiry"
        this.sendVerificationCodeURL = this.baseURL + "/send_verification_code"
        this.verifyPhoneURL = this.baseURL + "/verify_phone"
        this.refinedValuationResearchURL = this.baseURL + "/refined_valuation_research"
        this.listRefinedValuationsURL = this.baseURL + "/list_refined_valuations"
        this.getRefinedValuationURL = this.baseURL + "/get_refined_valuation"
        this.deleteRefinedValuationURL = this.baseURL + "/delete_refined_valuation"
        this.updateRefinedValuationURL = this.baseURL + "/update_refined_valuation"
        this.searchComparablesURL = this.baseURL + "/search_comparables"
        this.getListingPhotosURL = this.baseURL + "/get_listing_photos"
        this.consumeListingURL = this.baseURL + "/consume_listing"
        this.loginHelper = loginHelper;
        this.stateHandler = stateHandler;
        this.requestCache = new Map();
        this.featureProvider = featureProvider;
    }

    clearCache() {
        this.requestCache.clear();
    }

    getCacheKey(url: string, input: any): string {
        return `${url}-${JSON.stringify(input)}`;
    }

    async getAddress(input: { geo_id: string } | { unique_id: string } | { unique_location_id: string }) {
        return await this.postOrLogin(this.getAddressURL, input)
    }

    async getComparableHomes(input: { geo_id: string } | { unique_id: string } | { address: string }) {
        return await this.postOrLogin(this.comparableHomesURL, input)
    }

    async getValuation(input: any)  {
        return await this.postOrLogin(this.valuateURL, input)
    }

    async findAddress(input: any) {
        return await this.postOrLogin(this.findAddressURL, input)
    }

    async suggestAddress(input: any) {
        return await this.postOrLogin(this.suggestAddressURL, input)
    }

    async getUserInfo() {
        return await this.postOrLogin(this.getUserInfoURL, {})
    }

    async submitRefinedValuationResearch(input: any) {
        return await this.postOrLogin(this.refinedValuationResearchURL, input, true)
    }

    /** Fixed dimensions for card thumbnails so cache keys stay consistent. */
    static readonly THUMBNAIL_WIDTH = 160
    static readonly THUMBNAIL_HEIGHT = 120
    /** Fixed dimensions for the hero street-view on the valuation page. */
    static readonly HERO_WIDTH = 640
    static readonly HERO_HEIGHT = 360

    /**
     * Every distinct (width, height) that reaches `/get_address_image` creates a
     * separate S3 cache entry and a separate Streetview billable request. To
     * keep costs bounded we only permit a small allowlist of sizes; any other
     * pair is a coding bug and must fail loudly rather than quietly minting a
     * new cached size.
     */
    private static readonly ALLOWED_IMAGE_DIMENSIONS: ReadonlyArray<{ width: number; height: number }> = [
        { width: ApiHandler.THUMBNAIL_WIDTH, height: ApiHandler.THUMBNAIL_HEIGHT },
        { width: ApiHandler.HERO_WIDTH, height: ApiHandler.HERO_HEIGHT },
    ]

    /** Delay between consecutive /get_address_image requests, to avoid stampeding cold lambdas. */
    private static readonly IMAGE_REQUEST_GAP_MS = 100

    async getAddressImage(input: any) {
        const isAllowedSize = ApiHandler.ALLOWED_IMAGE_DIMENSIONS.some(
            (d) => d.width === input.width && d.height === input.height,
        )
        if (!isAllowedSize) {
            const allowed = ApiHandler.ALLOWED_IMAGE_DIMENSIONS.map((d) => `${d.width}x${d.height}`).join(", ")
            throw new Error(
                `getAddressImage refused: dimensions ${input.width}x${input.height} are not in the allowlist (${allowed}). ` +
                `Every unique size mints a new S3 cache entry AND a new Streetview billable request. ` +
                `If you genuinely need a new size, add it to ApiHandler.ALLOWED_IMAGE_DIMENSIONS and its matching constant.`,
            )
        }

        const cacheKey = `img_${input.location_id}_${input.width}_${input.height}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) return JSON.parse(cached);
        } catch {}

        const inFlight = this.inFlightImages.get(cacheKey);
        if (inFlight) return inFlight;

        const next = this.imageQueue.then(async () => {
            try {
                const cached = sessionStorage.getItem(cacheKey);
                if (cached) return JSON.parse(cached);
            } catch {}

            const response = await this.postOrLogin(this.getAddressImageURL, input);

            if (response?.raw_image && response.valid_image === true) {
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(response));
                } catch {}
            }

            return response;
        });

        this.inFlightImages.set(cacheKey, next);
        this.imageQueue = next
            .catch(() => {})
            .then(() => new Promise(r => setTimeout(r, ApiHandler.IMAGE_REQUEST_GAP_MS)));
        next.finally(() => {
            if (this.inFlightImages.get(cacheKey) === next) {
                this.inFlightImages.delete(cacheKey);
            }
        });

        return next;
    }

    /**
     * Get a cached address thumbnail with fixed size for use in cards.
     * Always uses the same dimensions so backend and client caching stay consistent.
     */
    async getAddressThumbnail(locationId: string, locationString: string) {
        return await this.getAddressImage({
            location_id: locationId,
            location_string: locationString,
            width: ApiHandler.THUMBNAIL_WIDTH,
            height: ApiHandler.THUMBNAIL_HEIGHT,
            return_saved: true,
        })
    }

    /**
     * Get the valuation-page hero street-view image at a fixed size.
     * Always uses the same dimensions so backend and client caching stay consistent.
     */
    async getAddressHeroImage(locationId: string, locationString: string) {
        return await this.getAddressImage({
            location_id: locationId,
            location_string: locationString,
            width: ApiHandler.HERO_WIDTH,
            height: ApiHandler.HERO_HEIGHT,
            return_saved: true,
        })
    }

    async saveValuation(input: any) {
        return await this.postOrLogin(this.saveValuationURL, input)
    }

    async updateSavedValuationValue(input: any) {
        return await this.postOrLogin(this.updateSavedValuationValueURL, input)
    }

    async listRefinedValuations(input: { state?: string; user_id?: string; unique_id?: string; limit?: number; include_archived?: boolean }) {
        return await this.postOrLogin(this.listRefinedValuationsURL, input, true, false)
    }

    async deleteRefinedValuation(input: { id: string }) {
        return await this.postOrLogin(this.deleteRefinedValuationURL, input, true, false)
    }

    async searchComparables(input: {
        unique_id: string
        limit?: number
        radius_metres?: number | null
        sold_within_months?: number | null
        minimum_beds?: number
        maximum_beds?: number
        minimum_baths?: number
        maximum_baths?: number
        minimum_sqm?: number
        maximum_sqm?: number
        minimum_ber_rating?: string
        maximum_ber_rating?: string
        property_types?: string[]
        with_photos_only?: boolean
    }) {
        return await this.postOrLogin(this.searchComparablesURL, input, true, false)
    }

    /** Photos are cached: the same ids return the same bytes, and they are large. */
    async getListingPhotos(input: { photo_ids: string[] }) {
        return await this.postOrLogin(this.getListingPhotosURL, input, true)
    }

    /** Never cached: it writes, and the same url consumed twice is a real second request. */
    async consumeListing(input: { url: string; unique_id: string; sale_id?: string }) {
        return await this.postOrLogin(this.consumeListingURL, input, true, false)
    }

    async updateRefinedValuationAnalysis(input: { id: string; analysis: any }) {
        return await this.postOrLogin(this.updateRefinedValuationURL, input, true, false)
    }

    async submitRefinedValuation(input: { id: string }) {
        return await this.postOrLogin(
            this.updateRefinedValuationURL, { ...input, state: "submitted" }, true, false,
        )
    }

    async archiveRefinedValuation(input: { id: string; archived: boolean }) {
        return await this.postOrLogin(this.updateRefinedValuationURL, input, true, false)
    }

    async getRefinedValuation(input: { id: string }) {
        return await this.postOrLogin(this.getRefinedValuationURL, input, true, false)
    }

    async listValuations(input: any, preventForceLogin: boolean = false) {
        return await this.postOrLogin(this.listValuationsURL, input, preventForceLogin)
    }

    async deleteValuation(input: any) {
        return await this.postOrLogin(this.deleteValuationURL, input)
    }

    async createCheckout(email?: string) {
        const input = email ? { email } : {}
        const cacheKey = this.getCacheKey(this.createCheckoutURL, input)
        this.requestCache.delete(cacheKey)
        return await this.postOrLogin(this.createCheckoutURL, input, true)
    }

    async findArea(input: { id?: string; text?: string }) {
        return await this.postOrLogin(this.findAreaURL, input)
    }

    async suggestArea(input: { area: string }) {
        return await this.postOrLogin(this.suggestAreaURL, input)
    }

    async claimPayment(paymentIntentId: string) {
        return await this.postOrLogin(this.baseURL + "/claim_payment", { payment_intent_id: paymentIntentId })
    }

    async getGlobalUsage() {
        return await this.apiRequester.post(this.globalUsageURL, {})
    }

    async getAreaSignals(input: { geo_id: string } | { unique_id: string } | { address: string }) {
        return await this.postOrLogin(this.areaSignalsURL, input)
    }

    async getNearbyAgents(input: { geo_id: string } | { unique_id: string }) {
        const idValue = "geo_id" in input ? input.geo_id : input.unique_id
        const sessionCacheKey = `nearby_agents_${idValue}`
        try {
            const cached = sessionStorage.getItem(sessionCacheKey)
            if (cached) return JSON.parse(cached)
        } catch {}

        const requestCacheKey = this.getCacheKey(this.nearbyAgentsURL, input)
        if (this.requestCache.has(requestCacheKey)) {
            return await this.requestCache.get(requestCacheKey)
        }

        const request = this.apiRequester.post(this.nearbyAgentsURL, input)
            .then(response => {
                try {
                    sessionStorage.setItem(sessionCacheKey, JSON.stringify(response))
                } catch {}
                return response
            })
            .catch(error => {
                this.requestCache.delete(requestCacheKey)
                throw error
            })
        this.requestCache.set(requestCacheKey, request)
        return await request
    }

    async getAgentLogo(input: { agent_id: string }) {
        const cacheKey = `agent_logo_${input.agent_id}`
        try {
            const cached = sessionStorage.getItem(cacheKey)
            if (cached) return JSON.parse(cached)
        } catch {}

        const inFlight = this.inFlightAgentLogos.get(cacheKey)
        if (inFlight) return inFlight

        const next = this.agentLogoQueue.then(async () => {
            try {
                const cached = sessionStorage.getItem(cacheKey)
                if (cached) return JSON.parse(cached)
            } catch {}

            const response = await this.apiRequester.post(this.getAgentLogoURL, input)
            if (response?.raw_image && response.valid_image === true) {
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify(response))
                } catch {}
            }
            return response
        })

        this.inFlightAgentLogos.set(cacheKey, next)
        this.agentLogoQueue = next
            .catch(() => {})
            .then(() => new Promise(resolve => setTimeout(resolve, 100)))
        next.finally(() => {
            if (this.inFlightAgentLogos.get(cacheKey) === next) {
                this.inFlightAgentLogos.delete(cacheKey)
            }
        })
        return next
    }

    async submitAgentEnquiry(input: any) {
        return await this.apiRequester.post(this.agentEnquiryURL, input)
    }

    async sendVerificationCode(phoneNumber: string) {
        return await this.apiRequester.post(this.sendVerificationCodeURL, { phone_number: phoneNumber })
    }

    async verifyPhone(phoneNumber: string, code: string) {
        return await this.apiRequester.post(this.verifyPhoneURL, { phone_number: phoneNumber, code })
    }

    async getValuationUsage() {
        const cacheKey = this.getCacheKey(this.valuationUsageURL, {})
        this.requestCache.delete(cacheKey)
        return await this.postOrLogin(this.valuationUsageURL, {}, true)
    }

    async postOrLogin(url: string, input: any, preventForceLogin: boolean = false, useCache: boolean = true) {
        const cacheKey = this.getCacheKey(url, input);

        if (useCache && this.requestCache.has(cacheKey)) {
            return this.requestCache.get(cacheKey);
        }

        let response 
        try {
            let tokenHeader = await this.getTokenHeaderOrLogin()
            response = await this.apiRequester.post(url, input, tokenHeader)
            if (response?.metadata?.li == false) {
                try {
                    await this.loginHelper.logout()
                } catch (error) {
                    // Ignore error
                }
                this.stateHandler.set(StateConstants.IS_LOGGED_IN, false)
            }
            
            if (useCache) {
                this.requestCache.set(cacheKey, response);
            }
            
        } catch(error) {
            this.captureApiError(url, error)
            if (error instanceof AuthenticationError) {
                if (preventForceLogin) {
                    return null
                }
                await this.loginHelper.logout()
                this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                throw new RedirectToLoginException("User is not authenticated, redirecting to login.")
            } else {
                throw error
            }
        }
        if (response?.metadata?.gb) {
            this.stateHandler.set(StateConstants.IS_GOOGLE_BOT, true)
        }
        return response
    }

    private captureApiError(url: string, error: any) {
        const endpoint = url.startsWith(this.baseURL) ? url.slice(this.baseURL.length) : url
        const status = typeof error?.status === "number" ? error.status : undefined
        posthog?.capture("api_error", {
            endpoint,
            status,
            error_type: error?.name ?? "Error",
            error_message: typeof error?.message === "string" ? error.message.slice(0, 200) : undefined,
        })
    }

    async getTokenHeaderOrLogin() {
        let isAuthRequired: FeatureFlag = this.featureProvider.getFeatureFlag(FeatureFlagIds.REQUIRE_AUTHENTICATION);
        let token: any = await this.loginHelper.getValidToken()
        if (token && Object.keys(token).length > 0) {
            return {
                "x-auth-token": token["id_token"],
                "x-auth-access-token": token["access_token"]
            }
        }
        if (!isAuthRequired.isEnabled()) {
            return {}
        }
        throw new AuthenticationError("User is not logged in, no valid authentication token.")
    }
}

export default ApiHandler
